import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';

import type { HistogramMetric } from 'constants/charting';
import type { OrderBookSettings } from 'react_pages/Event/components/MarketTabs/Orderbook/OrderBook';
import type { EventPageSocialSectionTab } from 'react_pages/Event/components/Sections/SocialSection/SocialSection';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from 'utils/storage';

const ORDER_BOOK_SETTINGS_KEY = 'Kalshi-OrderBookSettings';
const HISTOGRAM_METRIC_KEY = 'histogram-metric';
const FORECAST_GRAPH_HIDDEN_KEY = 'forecast-graph-hidden';
const FORECAST_GRAPH_CANDLESTICKS_KEY = 'forecast-graph-candlesticks';
const SOCIAL_SECTION_TAB_KEY = 'Kalshi-EventPageSocialSectionTab';
const RECENTLY_VISITED_EVENT_TICKERS_KEY = 'Kalshi-RecentlyVisitedEventTickers';

const DEFAULT_ORDER_BOOK_SETTINGS: OrderBookSettings = {
    showFullOrderbook: false,
    showMakerOrTaker: 'oneSided',
    showSellPrices: false,
    headerExpanded: false,
    collapseToCents: false,
    hideFractionalShares: false,
    showIncentiveDetails: true,
};

const MAX_RECENTLY_VISITED_EVENT_TICKERS = 4;

type PersistedState = {
    orderBookSettings: OrderBookSettings;
    histogramMetric: HistogramMetric;
    forecastGraphHidden: boolean;
    forecastGraphCandlesticks: boolean;
    eventPageSocialSectionSelectedTab: EventPageSocialSectionTab;
    recentlyVisitedEventTickers: string[];
};

interface EventPageUiState extends PersistedState {
    expandedMarketIdGraphOverride: string | null;
    showYearlyAdvancedChart: boolean;
    actions: {
        setOrderBookSettings: (settings: OrderBookSettings) => void;
        setHistogramMetric: (metric: HistogramMetric) => void;
        setForecastGraphHidden: (hidden: boolean) => void;
        setForecastGraphCandlesticks: (candlesticks: boolean) => void;
        setEventPageSocialSectionSelectedTab: (tab: EventPageSocialSectionTab) => void;
        insertRecentlyVisitedEventTicker: (ticker: string) => void;
        setExpandedMarketIdGraphOverride: (marketId: string | null) => void;
        setShowYearlyAdvancedChart: (show: boolean) => void;
    };
}

/**
 * The legacy path wrote each field to its own plain (non-JSON-envelope) localStorage key, so
 * this adapter fans one persisted slice out across all of them instead of zustand's default
 * single-key-per-store behavior — preserving existing users' saved preferences across the cutover.
 */
const legacyAwareStorage: PersistStorage<PersistedState> = {
    getItem: () => {
        let orderBookSettings = DEFAULT_ORDER_BOOK_SETTINGS;
        try {
            const raw = getLocalStorageItem(ORDER_BOOK_SETTINGS_KEY);
            if (raw) orderBookSettings = JSON.parse(raw);
        } catch {
            orderBookSettings = DEFAULT_ORDER_BOOK_SETTINGS;
        }

        const storedSocialSectionTab = getLocalStorageItem(SOCIAL_SECTION_TAB_KEY);
        const eventPageSocialSectionSelectedTab: EventPageSocialSectionTab =
            storedSocialSectionTab === 'top-holders' || storedSocialSectionTab === 'activity'
                ? storedSocialSectionTab
                : 'ideas';

        return {
            state: {
                orderBookSettings,
                histogramMetric: (getLocalStorageItem(HISTOGRAM_METRIC_KEY) as HistogramMetric) || null,
                forecastGraphHidden: (getLocalStorageItem(FORECAST_GRAPH_HIDDEN_KEY) || 'false') === 'true',
                forecastGraphCandlesticks: (getLocalStorageItem(FORECAST_GRAPH_CANDLESTICKS_KEY) || 'true') === 'true',
                eventPageSocialSectionSelectedTab,
                recentlyVisitedEventTickers: (getLocalStorageItem(RECENTLY_VISITED_EVENT_TICKERS_KEY) || '')
                    .split(',')
                    .filter(Boolean),
            },
            version: 0,
        };
    },
    setItem: (_name, value) => {
        setLocalStorageItem(ORDER_BOOK_SETTINGS_KEY, JSON.stringify(value.state.orderBookSettings));
        if (value.state.histogramMetric) {
            setLocalStorageItem(HISTOGRAM_METRIC_KEY, value.state.histogramMetric);
        } else {
            removeLocalStorageItem(HISTOGRAM_METRIC_KEY);
        }
        setLocalStorageItem(FORECAST_GRAPH_HIDDEN_KEY, value.state.forecastGraphHidden ? 'true' : 'false');
        setLocalStorageItem(FORECAST_GRAPH_CANDLESTICKS_KEY, value.state.forecastGraphCandlesticks ? 'true' : 'false');
        setLocalStorageItem(SOCIAL_SECTION_TAB_KEY, value.state.eventPageSocialSectionSelectedTab);
        setLocalStorageItem(RECENTLY_VISITED_EVENT_TICKERS_KEY, value.state.recentlyVisitedEventTickers.join(','));
    },
    removeItem: () => {
        removeLocalStorageItem(ORDER_BOOK_SETTINGS_KEY);
        removeLocalStorageItem(HISTOGRAM_METRIC_KEY);
        removeLocalStorageItem(FORECAST_GRAPH_HIDDEN_KEY);
        removeLocalStorageItem(FORECAST_GRAPH_CANDLESTICKS_KEY);
        removeLocalStorageItem(SOCIAL_SECTION_TAB_KEY);
        removeLocalStorageItem(RECENTLY_VISITED_EVENT_TICKERS_KEY);
    },
};

export const useEventPageUiStore = create<EventPageUiState>()(
    persist(
        (set, get) => ({
            orderBookSettings: DEFAULT_ORDER_BOOK_SETTINGS,
            histogramMetric: null,
            forecastGraphHidden: false,
            forecastGraphCandlesticks: true,
            eventPageSocialSectionSelectedTab: 'ideas',
            recentlyVisitedEventTickers: [],
            expandedMarketIdGraphOverride: null,
            showYearlyAdvancedChart: false,
            actions: {
                setOrderBookSettings: (settings) => set({ orderBookSettings: settings }),
                setHistogramMetric: (metric) => set({ histogramMetric: metric }),
                setForecastGraphHidden: (hidden) => set({ forecastGraphHidden: hidden }),
                setForecastGraphCandlesticks: (candlesticks) => set({ forecastGraphCandlesticks: candlesticks }),
                setEventPageSocialSectionSelectedTab: (tab) => set({ eventPageSocialSectionSelectedTab: tab }),
                insertRecentlyVisitedEventTicker: (ticker) => {
                    const next = get().recentlyVisitedEventTickers.filter((t) => t !== ticker);
                    next.push(ticker);
                    if (next.length > MAX_RECENTLY_VISITED_EVENT_TICKERS) {
                        next.shift();
                    }
                    set({ recentlyVisitedEventTickers: next });
                },
                setExpandedMarketIdGraphOverride: (marketId) => set({ expandedMarketIdGraphOverride: marketId }),
                setShowYearlyAdvancedChart: (show) => set({ showYearlyAdvancedChart: show }),
            },
        }),
        { name: ORDER_BOOK_SETTINGS_KEY, storage: legacyAwareStorage },
    ),
);
